import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  AlertBannerCodeMap,
  AlertBannerContext,
  KivuI18nContext,
  LOCALE,
  localizedPath,
  useKivunovaTranslation,
  isGraphQLHandledAlertError
} from "@kivunova/kivufrontendcommon";
import {
  getPartialSuccessPath,
  getSuccessPath,
  ROUTE_SUBSCRIPTIONS
} from "../constants/routes";
import {
  useCreateMembershipPayment,
  useCreateIremboInvoice,
  useReconcileIremboPayment
} from "../MembershipMFE/hooks/api/usePayment";
import { useGetSubscriptionList } from "./api/useSubscription";
import { useRefreshMembershipAfterPayment } from "./api/useRefreshMembershipAfterPayment";
import {
  getIremboPublicKey,
  iremboLanguageFromUi,
  uiLocaleFromCandidates
} from "../Utils/iremboMembershipEnv";
import {
  initiateIremboPayModal,
  isIremboWidgetAlreadyPaidError
} from "../Utils/iremboPayWidget";
import { toBannerCode } from "../Utils/toBannerCode";
import type { ReconcileIremboPaymentResponse } from "../Graphql/types/payment";
import type { RootState } from "../store/store";

import { LOG_LEVEL, buildLogContext, sendAppLog } from "../Utils/appLogger";

const RECONCILE_POLL_INTERVAL_MS = 3_000;
const RECONCILE_POLL_MAX_ATTEMPTS = 120;
const CHECKOUT_ABANDON_TIMEOUT_MS = 600_000;

/** Default MoMo rail for server-side payment creation; Irembo widget handles payer interaction. */
export const DEFAULT_MEMBERSHIP_PAYMENT_METHOD = "MTN_MOMO";

async function pollReconcileUntilPaid(
  reconcile: (input: {
    subscriptionId: string;
    invoiceId: string;
  }) => Promise<ReconcileIremboPaymentResponse>,
  subscriptionId: string,
  invoiceId: string,
  shouldContinue: () => boolean
): Promise<ReconcileIremboPaymentResponse | null> {
  for (let attempt = 0; attempt < RECONCILE_POLL_MAX_ATTEMPTS; attempt++) {
    if (!shouldContinue()) {
      return null;
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, RECONCILE_POLL_INTERVAL_MS);
    });
    if (!shouldContinue()) {
      return null;
    }
    try {
      const recRes = await reconcile({ subscriptionId, invoiceId });
      if (recRes.reconcileIremboPayment?.code === "OK") {
        return recRes;
      }
    } catch {
      // Irembo may not report PAID immediately after MoMo approval.
    }
  }
  return null;
}

/** User-facing copy aligned with the global alert banner (no raw API codes). */
function messageForBannerCode(
  code: ALERT_BANNER_CODE,
  t: (key: string, defaultValue: string) => string
): string {
  const { translationKey, value } = AlertBannerCodeMap[code];
  return t(translationKey, value);
}

export type StartCheckoutOptions = {
  /**
   * Called after payment + Irembo invoice are ready, immediately before the widget opens.
   * Use to dismiss a parent dialog so only the Irembo overlay is visible.
   */
  onInvoiceReady?: () => void;
  /**
   * Optional: duplicate checkout errors into parent UI (e.g. modal Alert) while it is open.
   * Errors are always shown on the global alert banner as well, including after the modal closes.
   */
  onCheckoutError?: (message: string) => void;
  /** Where to navigate after a successful payment (default: subscriptions list). */
  successDestination?: "subscriptions" | "success";
  /**
   * When true, incomplete payment redirects to the partial-success page instead of
   * showing only an error banner.
   */
  redirectOnIncomplete?: boolean;
};

export function useIremboSubscriptionPaymentFlow() {
  const navigate = useNavigate();
  const { t, i18n } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const activeLocale = uiLocaleFromCandidates(
    i18n?.resolvedLanguage,
    i18n?.language,
    language,
    defaultLang
  );
  const { setAlert } = useContext(AlertBannerContext);
  const createMembershipPaymentMutation = useCreateMembershipPayment();
  const createIremboInvoiceMutation = useCreateIremboInvoice();
  const reconcileIremboPaymentMutation = useReconcileIremboPayment();
  const refreshSubscriptionList = useGetSubscriptionList();
  const { refreshMembershipAfterPayment } = useRefreshMembershipAfterPayment();
  const [isRunning, setIsRunning] = useState(false);
  const companyIdForLogging = useSelector(
    (state: RootState) => state.user.userDetails?.companyId
  );

  const showBannerError = useCallback(
    (
      message: string,
      code: ALERT_BANNER_CODE = ALERT_BANNER_CODE.INTERNAL_ERROR
    ) => {
      setAlert({ severity: ALERT_BANNER_CODE_SEVERITY.ERROR, code });
    },
    [setAlert]
  );

  const startCheckout = useCallback(
    async (
      subscriptionId: string,
      options?: StartCheckoutOptions
    ): Promise<boolean> => {
      setIsRunning(true);
      const redirectOnIncomplete = options?.redirectOnIncomplete === true;
      const successRoute =
        options?.successDestination === "success"
          ? getSuccessPath(subscriptionId)
          : ROUTE_SUBSCRIPTIONS;

      const handleIncomplete = async (failureReason: string) => {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Subscription payment incomplete during checkout",
          buildLogContext(
            "subscriptions",
            "checkout-payment-incomplete",
            undefined,
            companyIdForLogging,
            undefined,
            { subscriptionId, failureReason }
          )
        );
        if (redirectOnIncomplete) {
          navigate(
            localizedPath(
              getPartialSuccessPath(subscriptionId),
              activeLocale,
              defaultLang ?? LOCALE.EN
            ),
            { replace: true }
          );
          return false;
        }
        return null;
      };

      /** Surface errors on the global banner unless redirecting to partial success. */
      const notifyError = (
        message: string,
        code: ALERT_BANNER_CODE = ALERT_BANNER_CODE.INTERNAL_ERROR
      ) => {
        if (!redirectOnIncomplete) {
          showBannerError(message, code);
        }
        options?.onCheckoutError?.(message);
      };

      try {
        const publicKey = getIremboPublicKey();
        if (!publicKey) {
          const message = t(
            "checkout-public-key-missing",
            "Payment gateway is not fully configured."
          );
          if (redirectOnIncomplete) {
            return (
              (await handleIncomplete(
                "Irembo Pay public key is not configured"
              )) ?? false
            );
          }
          notifyError(message, ALERT_BANNER_CODE.VALIDATION_ERROR);
          return false;
        }

        const payRes = await createMembershipPaymentMutation.mutateAsync({
          subscriptionId,
          paymentMethod: DEFAULT_MEMBERSHIP_PAYMENT_METHOD
        });
        const payment = payRes.createMembershipPayment?.payment;
        if (
          payRes.createMembershipPayment?.code !== "OK" ||
          !payment?.paymentId
        ) {
          const bannerCode = toBannerCode(payRes.createMembershipPayment?.code);
          const message = messageForBannerCode(bannerCode, t);
          if (redirectOnIncomplete) {
            return (await handleIncomplete(message)) ?? false;
          }
          notifyError(message, bannerCode);
          return false;
        }

        const paymentId = payment.paymentId;
        const invRes = await createIremboInvoiceMutation.mutateAsync({
          paymentId,
          language: iremboLanguageFromUi(activeLocale)
        });

        if (invRes.createIremboInvoice?.code !== "OK") {
          const bannerCode = toBannerCode(invRes.createIremboInvoice?.code);
          const message = messageForBannerCode(bannerCode, t);
          if (redirectOnIncomplete) {
            return (await handleIncomplete(message)) ?? false;
          }
          notifyError(message, bannerCode);
          return false;
        }

        const iremboInv = invRes.createIremboInvoice.invoice;
        if (iremboInv?.subscriptionActivated) {
          options?.onInvoiceReady?.();
          await refreshMembershipAfterPayment({
            membershipTokenIssued:
              invRes.createIremboInvoice?.membershipTokenIssued
          });
          await refreshSubscriptionList.mutateAsync({});
          navigate(
            localizedPath(successRoute, activeLocale, defaultLang ?? LOCALE.EN),
            {
              replace: true,
              state: {
                paymentId,
                subscriptionId,
                fromCompletedPayment: true
              }
            }
          );
          return true;
        }

        const invoiceNumber = iremboInv?.invoiceNumber?.trim();
        if (!invoiceNumber) {
          const message = t(
            "checkout-no-invoice-number",
            "Invoice was not created. Check IremboPay configuration."
          );
          if (redirectOnIncomplete) {
            return (await handleIncomplete(message)) ?? false;
          }
          notifyError(message);
          return false;
        }

        options?.onInvoiceReady?.();

        let earlyReconcileResult: ReconcileIremboPaymentResponse | null = null;
        const paymentConfirmed = await new Promise<boolean>((resolve) => {
          let settled = false;
          const confirm = (recRes?: ReconcileIremboPaymentResponse) => {
            if (settled) {
              return;
            }
            settled = true;
            window.clearTimeout(abandonTimer);
            if (recRes) {
              earlyReconcileResult = recRes;
            }
            resolve(true);
          };
          const reject = () => {
            if (settled) {
              return;
            }
            settled = true;
            window.clearTimeout(abandonTimer);
            resolve(false);
          };

          void pollReconcileUntilPaid(
            (input) => reconcileIremboPaymentMutation.mutateAsync(input),
            subscriptionId,
            invoiceNumber,
            () => !settled
          ).then((recRes) => {
            if (recRes) {
              try {
                window.IremboPay?.closeModal?.();
              } catch {
                /* ignore */
              }
              confirm(recRes);
            }
          });

          const abandonTimer = window.setTimeout(() => {
            reject();
          }, CHECKOUT_ABANDON_TIMEOUT_MS);

          void initiateIremboPayModal({
            t,
            publicKey,
            invoiceNumber,
            uiLanguage: activeLocale,
            onSuccess: () => {
              confirm();
            },
            onError: (msg, err) => {
              if (isIremboWidgetAlreadyPaidError(err)) {
                confirm();
                return;
              }
              if (!redirectOnIncomplete) {
                notifyError(msg, ALERT_BANNER_CODE.VALIDATION_ERROR);
              }
              // Keep polling: MoMo can complete after the widget closes.
            }
          });
        });
        if (!paymentConfirmed) {
          if (redirectOnIncomplete) {
            return (
              (await handleIncomplete("Payment widget closed or failed")) ??
              false
            );
          }
          return false;
        }

        const recRes =
          earlyReconcileResult ??
          (await reconcileIremboPaymentMutation.mutateAsync({
            subscriptionId,
            invoiceId: invoiceNumber
          }));
        if (recRes.reconcileIremboPayment?.code !== "OK") {
          const bannerCode = toBannerCode(recRes.reconcileIremboPayment?.code);
          const message = messageForBannerCode(bannerCode, t);
          if (redirectOnIncomplete) {
            return (await handleIncomplete(message)) ?? false;
          }
          notifyError(message, bannerCode);
          return false;
        }

        await refreshMembershipAfterPayment({
          membershipTokenIssued:
            recRes.reconcileIremboPayment?.membershipTokenIssued
        });
        await refreshSubscriptionList.mutateAsync({});

        navigate(
          localizedPath(successRoute, activeLocale, defaultLang ?? LOCALE.EN),
          {
            replace: true,
            state: {
              paymentId,
              subscriptionId,
              fromCompletedPayment: true
            }
          }
        );
        return true;
      } catch (error: unknown) {
        if (!isGraphQLHandledAlertError(error)) {
          const message = messageForBannerCode(
            ALERT_BANNER_CODE.SERVICE_UNAVAILABLE,
            t
          );
          if (redirectOnIncomplete) {
            return (await handleIncomplete(message)) ?? false;
          }
          notifyError(message, ALERT_BANNER_CODE.SERVICE_UNAVAILABLE);
        } else if (redirectOnIncomplete) {
          return (await handleIncomplete("Payment request failed")) ?? false;
        }
        return false;
      } finally {
        setIsRunning(false);
      }
    },
    [
      activeLocale,
      createIremboInvoiceMutation,
      createMembershipPaymentMutation,
      defaultLang,
      navigate,
      reconcileIremboPaymentMutation,
      refreshMembershipAfterPayment,
      refreshSubscriptionList,
      showBannerError,
      t
    ]
  );

  return { startCheckout, isRunning };
}
